import { ListChecks, RotateCcw } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { AwardCategoryModal } from '../components/AwardCategoryModal';
import { awardCategoriesApi } from '../praiseApi';
import {
  AWARD_CATEGORY_AUDIT_ENTITY_TYPES,
  type AwardCategoryDetail,
  type AwardCategoryListItem,
} from '../praiseTypes';

/**
 * The PRAISE award categories — the six the program names, and whatever the agency adds.
 *
 * The anchor opens the **profile**, where the checklist is read; Edit is in the row menu and needs
 * the detail, so it fetches it first — the list row does not carry the lines.
 */
export function AwardCategoriesIndexPage() {
  const navigate = useNavigate();
  const toast = useToast();

  const [editing, setEditing] = useState<{ detail: AwardCategoryDetail; section: 'details' | 'checklist' } | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState<{ kind: 'deactivate' | 'activate'; category: AwardCategoryListItem } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchCategories = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      awardCategoriesApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
        },
        signal,
      ),
    [],
  );

  // Either section's edit fetches the DETAIL first; a failed fetch has no form to report in, so it toasts.
  const openSection = useCallback(
    (category: AwardCategoryListItem, section: 'details' | 'checklist') => {
      awardCategoriesApi
        .get(category.id)
        .then((detail) => setEditing({ detail, section }))
        .catch((caught: unknown) => toast.failure(caught instanceof ApiError ? caught.message : 'Could not load this category.'));
    },
    [toast],
  );

  const rowActions = useCallback(
    (category: AwardCategoryListItem): MenuItem[] => [
      {
        key: `edit-${category.id}`,
        label: 'Edit',
        permission: 'award-categories.update',
        onClick: () => openSection(category, 'details'),
      },
      {
        key: `edit-checklist-${category.id}`,
        label: 'Edit checklist',
        icon: <ListChecks className="h-4 w-4" aria-hidden="true" />,
        permission: 'award-categories.update',
        onClick: () => openSection(category, 'checklist'),
      },
      {
        key: `restore-${category.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'award-categories.delete',
        disabledReason: category.isActive ? 'This category is already active.' : null,
        onClick: () => setConfirming({ kind: 'activate', category }),
      },
    ],
    [openSection],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'deactivate') await awardCategoriesApi.deactivate(confirming.category.id);
      else await awardCategoriesApi.activate(confirming.category.id);

      toast.success(`${confirming.category.name} was ${confirming.kind === 'deactivate' ? 'deleted' : 'restored'}.`);
      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<AwardCategoryListItem>
        module="award-categories"
        // Reached through PRAISE → Settings, so Settings links: that crumb is the way back.
        breadcrumb={['Human Resources', 'PRAISE', { label: 'Settings', to: '/praise/settings' }, 'Award Categories']}
        columns={COLUMNS}
        facets={[{ key: 'status', label: 'Status' }]}
        auditEntityTypes={AWARD_CATEGORY_AUDIT_ENTITY_TYPES}
        rowKey={(category) => category.id}
        fetch={fetchCategories}
        reloadKey={reloadKey}
        searchPlaceholder="Search categories or the documents they ask for"
        emptyMessage="No award category yet. Add the kinds of award, with the documents each asks of a nominee."
        onCreate={() => setCreating(true)}
        createPermission="award-categories.create"
        onRowOpen={(category) => navigate(`/praise/settings/award-categories/${category.id}`)}
        openPermission="award-categories.read"
        rowActions={rowActions}
        onDelete={(category) => setConfirming({ kind: 'deactivate', category })}
        deletePermission="award-categories.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(category) => (category.isActive ? null : 'This category is already deleted.')}
        onClearData={async () => {
          const cleared = await awardCategoriesApi.clear();
          toast.success(`${cleared} ${cleared === 1 ? 'category was' : 'categories were'} deleted.`);
          return cleared;
        }}
        clearDataPermission="award-categories.clear-data"
        moduleNoun="award categories"
        otherInfo={(category) => category}
      />

      {creating || editing !== null ? (
        <AwardCategoryModal
          open
          section={editing?.section ?? 'create'}
          editing={editing?.detail ?? null}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={(_saved, options) => {
            refresh();
            if (options?.keepOpen) return;
            setCreating(false);
            setEditing(null);
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'deactivate' ? 'Delete category' : 'Restore category'}
          danger={confirming.kind === 'deactivate'}
          requireConfirm={confirming.kind === 'deactivate'}
          requireCredential={confirming.kind === 'deactivate'}
          loading={running}
          id={`award-category-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'deactivate'
                  ? `"${confirming.category.name}" stops being offered when an award is opened.`
                  : `"${confirming.category.name}" is offered again when an award is opened.`}
              </p>

              {confirming.kind === 'deactivate' && confirming.category.awardCount > 0 ? (
                <p>
                  {confirming.category.awardCount} award{confirming.category.awardCount === 1 ? ' is' : 's are'} of this
                  category. They keep naming it, and their nominees keep their documents.
                </p>
              ) : null}

              <p>Nothing is erased — a deleted category can be restored from this list.</p>

              {error ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<AwardCategoryListItem>[] = [
  {
    key: 'name',
    header: 'Name',
    width: 280,
    flex: true,
    sortable: true,
    render: (category) => <TextCell value={category.name} />,
  },
  {
    key: 'description',
    header: 'Description',
    width: 360,
    summary: true,
    render: (category) => <TextCell value={category.description} />,
  },
  {
    key: 'documentCount',
    header: 'Documents',
    width: 120,
    align: 'right',
    summary: true,
    render: (category) => <TextCell value={String(category.documentCount)} mono numeric />,
  },
  {
    key: 'awardCount',
    header: 'Awards',
    width: 110,
    align: 'right',
    // The count is of another module's records; the name and checklist beside it are this module's own.
    permission: 'award-nominations.read',
    render: (category) => <TextCell value={String(category.awardCount)} mono numeric />,
  },
  {
    // The stored sort value, under a heading that says so.
    key: 'sortOrder',
    header: 'Order',
    width: 90,
    align: 'right',
    sortable: true,
    render: (category) => <TextCell value={String(category.sortOrder)} mono numeric />,
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    render: (category) =>
      category.isActive ? <StatusText tone="active">Active</StatusText> : <StatusText tone="inactive">Deleted</StatusText>,
  },
];
